/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { ChangeEvent, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, FormControl, TextField } from '@mui/material';
import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteValue,
} from '@mui/material/useAutocomplete';

import { ServiceTypeMatrix } from 'shared';

import { Namespaces } from '../../../../i18n';
import { OrganizationVO, ProjectVO, SearchOption, ServiceVO } from '../../CreateClusterOverlay.types';
import { useSearchAutocompleteStyles } from './SearchAutocomplete.styles';

type SearchAutocompleteProps = {
  searchOptions: SearchOption[];
  onSelect: (params: { organization?: OrganizationVO; project?: ProjectVO; service?: ServiceVO }) => void;
  purpose: 'simple' | 'cluster_creation_from_backup';
};

export const SearchAutocomplete = memo<SearchAutocompleteProps>(props => {
  const { searchOptions, onSelect, purpose } = props;
  const classes = useSearchAutocompleteStyles();
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');

  const handleAutocompleteChange = useCallback(
    (
      _e: React.ChangeEvent<object>,
      value: AutocompleteValue<SearchOption | null, undefined, undefined, true>,
      reason: AutocompleteChangeReason,
    ) => {
      if (reason === 'selectOption' && value && typeof value !== 'string') {
        setInputValue('');
        onSelect({ ...value.entity });
      }
    },
    [onSelect],
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<any>, value: string, reason: AutocompleteInputChangeReason) =>
      reason === 'reset' ? setInputValue('') : setInputValue(value),
    [setInputValue],
  );

  return (
    <FormControl className={classes.formControl}>
      <Autocomplete
        classes={{ root: classes.searchWrapper }}
        onChange={handleAutocompleteChange}
        onInputChange={handleInputChange}
        inputValue={inputValue}
        value={null}
        renderInput={renderParams => (
          <TextField
            label={t('common:label.search')}
            {...renderParams}
            InputProps={{ ...renderParams.InputProps, startAdornment: <SearchIcon /> }}
          />
        )}
        options={searchOptions.sort((a, b) => -b.group.localeCompare(a.group))}
        groupBy={option => t(`${Namespaces.CreateCluster}:labels.${option.group}`)}
        getOptionDisabled={option =>
          purpose === 'cluster_creation_from_backup' &&
          option.group === 'services' &&
          !ServiceTypeMatrix.sourceClusterForCreatingPostgresqlFromBackup[option.entity.service.type]
        }
        getOptionLabel={option => {
          if (typeof option === 'object') {
            if (option.group === 'organizations') {
              return option.entity.organization.name;
            } else if (option.group === 'projects') {
              const organizationName = option.entity.organization.name;
              return `${option.entity.project.name} (${organizationName})`;
            } else if (option.group === 'services') {
              const projectName = 'project' in option.entity ? ` (${option.entity.project.name})` : '';
              return `${option.entity.service.name}${projectName}`;
            }
          }
          return option;
        }}
        freeSolo
      />
    </FormControl>
  );
});
